import React, { useState } from 'react';
import { ConsultarProprietarioResult } from '../types';
import { 
  ArrowLeft, 
  User,
  Building2,
  Car,
  FileText,
  Copy,
  CheckCircle,
  ShieldAlert,
  BadgeCheck,
  Calendar
} from 'lucide-react';

interface ConsultaProprietarioResultProps {
  data: ConsultarProprietarioResult;
  onBack: () => void;
}

const ConsultaProprietarioResult: React.FC<ConsultaProprietarioResultProps> = ({ data, onBack }) => {
  const [copied, setCopied] = useState<string | null>(null);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const { proprietario } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950/10 to-slate-950">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDI5M2EiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyek0yNiAyNGgtMnY0aDJ2LTR6bTAgNmgtMnY0aDJ2LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
      
      <div className="relative max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Voltar</span>
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-3xl border border-slate-700/50 overflow-hidden backdrop-blur-sm shadow-2xl">
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                <User className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white">Proprietário Atual</h1>
                <p className="text-indigo-100 text-sm mt-1">
                  Informações parciais do proprietário
                </p>
              </div>
            </div>
          </div>

          {/* Vehicle Plate */}
          <div className="p-6 border-b border-slate-700/50">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-800/50 rounded-xl">
                <Car className="w-6 h-6 text-slate-400" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-mono font-bold text-white tracking-wider">{data.placa}</p>
                <p className="text-sm text-slate-400">Placa consultada</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Consultado em</p>
                <p className="text-sm text-slate-400">{data.dataConsulta}</p>
              </div>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="mx-6 mt-6">
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-200">
                  <p className="font-medium">Informação Parcial</p>
                  <p className="text-amber-300/70 mt-1">
                    Esta consulta não permite identificação completa de pessoas físicas. 
                    Apenas o primeiro nome e início do documento são apresentados.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Owner Information */}
          <div className="p-6 space-y-6">
            {/* Owner Type Banner */}
            <div className={`p-5 rounded-2xl border ${
              proprietario.isPessoaFisica 
                ? 'bg-indigo-500/10 border-indigo-500/30' 
                : 'bg-violet-500/10 border-violet-500/30'
            }`}>
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${
                  proprietario.isPessoaFisica 
                    ? 'bg-indigo-500/20' 
                    : 'bg-violet-500/20'
                }`}>
                  {proprietario.isPessoaFisica ? (
                    <User className={`w-8 h-8 ${
                      proprietario.isPessoaFisica ? 'text-indigo-400' : 'text-violet-400'
                    }`} />
                  ) : (
                    <Building2 className="w-8 h-8 text-violet-400" />
                  )}
                </div>
                <div>
                  <span className={`text-sm font-medium ${
                    proprietario.isPessoaFisica ? 'text-indigo-400' : 'text-violet-400'
                  }`}>
                    {proprietario.isPessoaFisica ? 'Pessoa Física' : 'Pessoa Jurídica'}
                  </span>
                  <p className="text-2xl font-bold text-white mt-1">
                    {proprietario.nome}
                  </p>
                </div>
                <div className="ml-auto">
                  <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                    proprietario.isPessoaFisica 
                      ? 'bg-indigo-500/20 text-indigo-300' 
                      : 'bg-violet-500/20 text-violet-300'
                  }`}>
                    {proprietario.tipoDocumento}
                  </span>
                </div>
              </div>
            </div>

            {/* Document */}
            <div className="p-5 bg-slate-800/30 rounded-2xl border border-slate-700/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-400">
                      {proprietario.isPessoaFisica ? 'CPF' : 'CNPJ'} (parcial)
                    </p>
                    <p className="text-xl font-mono text-white mt-1 tracking-wider">
                      {proprietario.documentoMascarado}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => copyToClipboard(proprietario.documentoMascarado, 'doc')}
                  className={`p-2.5 rounded-xl transition-colors ${
                    copied === 'doc'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  {copied === 'doc' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Summary Card */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
                <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                  <BadgeCheck className="w-4 h-4" />
                  <span>Tipo</span>
                </div>
                <p className="text-white font-medium">
                  {proprietario.isPessoaFisica ? 'Pessoa Física' : 'Pessoa Jurídica'}
                </p>
              </div>
              <div className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
                <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                  <Calendar className="w-4 h-4" />
                  <span>Consulta</span>
                </div>
                <p className="text-white font-medium">
                  {new Date(data.dataConsulta.replace(' ', 'T')).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="p-4 bg-slate-800/20 rounded-xl border border-slate-700/30">
              <p className="text-xs text-slate-500 text-center">
                As informações apresentadas são fornecidas conforme disponíveis na base de dados consultada. 
                Para informações completas, consulte os órgãos oficiais competentes.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-slate-500 pb-4">
          Consulta realizada via Pedigree do Carro • {data.apiSource}
        </div>
      </div>
    </div>
  );
};

export default ConsultaProprietarioResult;

